import type { PostgresJsDatabase } from "drizzle-orm/postgres-js";

import { appEvents } from "./app-events";
import { accounts, userRelations, users } from "./auth";
import { blocks, pages } from "./blocks";
import { blogs } from "./blogs";
import { directorRelations, directors } from "./directors";
import { divisionRelations, divisions } from "./divisions";
import { faqs } from "./faqs";
import { helpArticles } from "./help-articles";
import { levels } from "./levels";
import { matchRefTeams, matchRefTeamsRelations } from "./match-ref-teams";
import { matchRefs, matchRefsRelations } from "./match-refs";
import { matchSets, matchSetsRelations } from "./match-sets";
import { paymentIntents } from "./payment-intents";
import { memberships, refunds } from "./payments";
import { playerProfileRelations, playerProfiles } from "./player-profiles";
import { playoffBracketTemplates } from "./playoff-bracket-templates";
import { playoffMatches, playoffMatchRelations } from "./playoff-matches";
import { poolMatches, poolMatchRelations } from "./pool-matches";
import { poolTeams, poolTeamsRelations } from "./pool-teams";
import { poolRelations, pools } from "./pools";
import { rateLimiterFlexibleSchema } from "./rate-limits";
import type { relations } from "./relations";
import { scoreEvents } from "./score-events";
import { teamPlayerRelations, teamPlayers } from "./team-players";
import { teamRelations, teams } from "./teams";
import {
  tournamentDirectorRelations,
  tournamentDirectors,
} from "./tournament-directors";
import {
  tournamentDivisionRequirements,
  tournamentDivisionRequirementsRelations,
} from "./tournament-division-requirements";
import {
  statusTimestamps,
  tournamentDivisionTeams,
  tournamentDivisionTeamsRelations,
} from "./tournament-division-teams";
import {
  tournamentDivisionRelations,
  tournamentDivisions,
} from "./tournament-divisions";
import { tournamentGalleryPhotos } from "./tournament-gallery-photos";
import {
  tournamentInviteSources,
  tournamentInviteSourcesRelations,
} from "./tournament-invite-sources";
import {
  tournamentInvites,
  tournamentInvitesRelations,
} from "./tournament-invites";
import { tournamentPlayerCheckIns } from "./tournament-player-check-ins";
import { tournamentRelations, tournaments } from "./tournaments";
import { legacyUsers, userProfiles } from "./user-profiles";
import { venueDirectorRelations, venueDirectors } from "./venue-directors";
import { venues, venuesRelations } from "./venues";

export * from "./app-events";
export * from "./auth";
export * from "./blocks";
export * from "./blogs";
export * from "./directors";
export * from "./divisions";
export * from "./faqs";
export * from "./help-articles";
export * from "./levels";
export * from "./match-ref-teams";
export * from "./match-refs";
export * from "./match-sets";
export * from "./payment-intents";
export * from "./payments";
export * from "./player-profiles";
export * from "./playoff-bracket-templates";
export * from "./playoff-matches";
export * from "./pool-matches";
export * from "./pool-teams";
export * from "./pools";
export * from "./rate-limits";
export * from "./relations";
export * from "./score-events";
export * from "./team-players";
export * from "./teams";
export * from "./tournament-directors";
export * from "./tournament-division-requirements";
export * from "./tournament-division-teams";
export * from "./tournament-divisions";
export * from "./tournament-gallery-photos";
export * from "./tournament-invite-sources";
export * from "./tournament-invites";
export * from "./tournament-player-check-ins";
export * from "./tournaments";
export * from "./user-profiles";
export * from "./venue-directors";
export * from "./venues";

export const tables = {
  appEvents,
  blocks,
  directors,
  divisions,
  levels,
  matchSets,
  matchRefTeams,
  matchRefs,
  pages,
  playerProfiles,
  playoffBracketTemplates,
  playoffMatches,
  poolMatches,
  pools,
  poolTeams,
  teamPlayers,
  teams,
  tournamentDirectors,
  tournamentDivisionRequirements,
  tournamentDivisions,
  tournamentDivisionTeams,
  tournamentGalleryPhotos,
  tournamentPlayerCheckIns,
  tournamentInviteSources,
  tournamentInvites,
  tournaments,
  users,
  accounts,
  venues,
  venueDirectors,
  rateLimiterFlexibleSchema,
  scoreEvents,
  faqs,
  helpArticles,
  blogs,
  memberships,
  paymentIntents,
  refunds,
  legacyUsers,
  userProfiles,
};

export { statusTimestamps };

export const relationships = {
  directorRelations,
  divisionRelations,
  matchSetsRelations,
  playerProfileRelations,
  playoffMatchRelations,
  poolMatchRelations,
  poolRelations,
  matchRefTeamsRelations,
  matchRefsRelations,
  poolTeamsRelations,
  teamPlayerRelations,
  teamRelations,
  tournamentDirectorRelations,
  tournamentDivisionRelations,
  tournamentDivisionRequirementsRelations,
  tournamentDivisionTeamsRelations,
  tournamentInviteSourcesRelations,
  tournamentInvitesRelations,
  tournamentRelations,
  venuesRelations,
  venueDirectorRelations,
  userRelations,
};

const schema = {
  ...tables,
  ...relationships,
};

export type Database = PostgresJsDatabase<typeof schema, typeof relations>;
export type Transaction = Parameters<Parameters<Database["transaction"]>[0]>[0];

/**
 * A database handle that works for both transactional and non-transactional
 * contexts. Pass this instead of `Transaction` when the caller may run outside
 * a transaction (e.g. TD operations that no longer need serialisation).
 */
export type Queryable = Database | Transaction;
